/**
 * Default copy and safe HTML for gallery category pages.
 * Shared by the server render and the client, so this file stays CommonJS
 * and only depends on the language runtime.
 */

const SUMMER_SPECIAL = {
  slug: 'family-summer-special',
  title: 'Family Summer Special | Noordhoek, Cape Town',
  description: 'Family summer special in Cape Town: a 45-minute golden-hour photoshoot near Noordhoek for R2,500, with an edited online gallery, until 15 December 2026.',
  categoryPath: '/galleries/family',
  categoryLinkLabel: 'Family photography in Cape Town',
};

const DEFAULT_CATEGORY_CONTENT = {
  family: {
    seoTitle: 'Family Photographer Cape Town | Relaxed Beach Shoots',
    seoDescription: 'Family photographer in Cape Town for relaxed beach photoshoots in Noordhoek, Kommetjie and Hout Bay. Natural photos of kids, parents and grandparents.',
    h1: 'Family Photographer in Cape Town',
    intro: [
      'I photograph families the way they really are: the toddler who won\'t stand still, the teenager who secretly loves a hug, the grandparents who have waited all year to have everyone in one place. My family shoots are relaxed, outdoors and unhurried. I don\'t line you up in front of a wall and ask for twenty identical smiles. Instead, we walk, play, chat and wander along the sand, and I photograph what happens in between.',
      'I live and work at the foot of Chapman\'s Peak in Noordhoek, so the beaches and mountains of the Cape Peninsula are my everyday studio. If you want photos that look like your family on a good day, with real light, real laughter and a bit of wind in your hair, you\'re in the right place.',
    ].join('\n\n'),
    body: [
      '## What a family shoot with me is like',
      'Before the shoot, we have a quick chat about who is coming, how old the children are and what you\'d love to have on your wall afterwards. I help you choose a location and a time of day that suit your family. I\'m happy to keep an eye on the weather with you, because in Cape Town the wind decides a lot.',
      'On the day, I give gentle guidance rather than stiff poses. I\'ll suggest a walk along the water\'s edge, a race to the dunes, a cuddle on a blanket or a piggyback for the smallest legs. Children usually relax within a few minutes once they realise this is more like an outing than a photoshoot. I also make sure to get the "proper" photos for the grandparents: everyone together, looking at the camera, nobody mid-sneeze.',
      'Kids, parents and grandparents are all welcome. Whether it\'s just the three of you or three generations at once, I plan the session so that everyone gets photographed, not only the people who are easiest to pose.',
      'Afterwards, I edit your photos and deliver them in a professionally edited online gallery that you can download and share with family.',
      '## Where I photograph families',
      'Most of my family sessions happen on the beaches and trails close to home:',
      '- Noordhoek Beach, a long, wide stretch of white sand with Chapman\'s Peak at one end. There\'s plenty of room for children to run, and the light at golden hour is beautiful.',
      '- Kommetjie, with its lighthouse, rocks and tidal pools for little explorers.',
      '- Fish Hoek, a calm, family-friendly beach on the False Bay side, often a good option when the wind is up on the Atlantic side.',
      '- Hout Bay, with mountain views, the harbour and a beach that\'s easy for prams and grandparents.',
      'I\'m also happy to travel to other Cape Town beaches, forests and gardens, or to a place that means something to your family. If you\'re not sure where to go, have a look at my guide to the [best family photo locations in Cape Town](/blog/family-photo-locations-cape-town).',
      'The best light is usually in the early morning or the last hour or two before sunset. In summer, an early session is often calmer and cooler, and a lot easier on small children.',
      '## What to wear',
      'Choose clothes that you feel comfortable in and that let you move, sit on the sand and pick up a child. A few things that work well:',
      '- Coordinate, don\'t match. Choose two or three colours that go together, such as soft neutrals, muted blues, sage or warm earthy tones, rather than identical white shirts and jeans.',
      '- Avoid big logos and busy prints, which pull attention away from faces.',
      '- Think about texture and movement. Linen, knitwear and dresses that move in the breeze photograph beautifully on the beach.',
      '- Bring a layer. Cape Town evenings can turn cool and windy quickly, and a jersey or light jacket keeps everyone happy.',
      '- Bare feet are perfect on the sand. Just bring towels and a change of clothes for anyone who ends up in the water, because somebody always does.',
      'When you book, I\'ll send you more detailed guidance on outfits and planning.',
      'This summer I am offering a short family session on a beach or nature spot near Noordhoek, in golden-hour light, until 15 December 2026. [See the Family Summer Special](/services/family-summer-special).',
      'Ready to plan your family shoot? [Get in touch](/contact) or call me on [082 415 1652](tel:+27824151652).',
    ].join('\n\n'),
    faqs: [
      {
        question: 'Where do family photoshoots take place?',
        answer: 'Most of my sessions are on the beaches around Noordhoek, Kommetjie, Fish Hoek and Hout Bay, but I photograph families all over Cape Town. A garden or a forest trail works well too. I\'ll help you choose a spot that suits your family and the weather.',
      },
      {
        question: 'What\'s the best time of day for family photos?',
        answer: 'Early morning or the last hour or two before sunset gives the softest, most flattering light. In summer I often suggest an early session, when it\'s cooler, calmer and the beaches are quieter.',
      },
      {
        question: 'What if it\'s windy or rains on the day?',
        answer: 'Cape Town weather has a mind of its own. I watch the forecast with you in the days before the shoot. If it looks too wild, we can talk about a more sheltered location or another date.',
      },
      {
        question: 'My children are very young or won\'t sit still. Is that a problem?',
        answer: 'Not at all. I don\'t expect anyone to sit still for long. The session is built around play and movement, and some of my favourite photos happen when a toddler runs off towards the waves. We go at your children\'s pace.',
      },
      {
        question: 'Can grandparents and the extended family join?',
        answer: 'Yes. Kids, parents and grandparents are all welcome. For a bigger group, tell me who is coming and I will plan enough time for everyone.',
      },
      {
        question: 'How do I book?',
        answer: 'Send me a message through the contact page or call or WhatsApp me on 082 415 1652 with your preferred dates, where you\'d like to shoot and who is coming. I\'ll confirm availability and help you with the rest.',
      },
    ],
  },
  'business-and-commercial': {
    seoTitle: 'Headshot & Business Photographer Cape Town | On Location',
    seoDescription: 'Natural-light headshots and business photography in Cape Town: LinkedIn profiles, personal branding, team portraits and brand content, on location.',
    h1: 'Headshot and Business Photographer in Cape Town',
    intro: [
      'I photograph headshots and business sessions the same way I photograph families: outside, in real light, without a row of identical poses. A LinkedIn portrait, a personal-brand session or a small team shoot should look like you on a good working day. We talk, walk and make the pictures while the conversation is still going.',
      'I live and work at the foot of Chapman\'s Peak in Noordhoek, and I come to you. Offices, studios, cafés, beaches and quiet corners of the Cape Peninsula are all useful. If you want photographs for a profile, a website or a brand, rather than a studio backdrop, you\'re in the right place.',
    ].join('\n\n'),
    body: [
      '## What a headshot or business session is like',
      'Before the shoot we have a short conversation about what the photographs are for. A LinkedIn profile, a company page, a pitch deck and a brand story need different pictures, and it helps to know which one you actually need. I also ask about the people in the frame, the clothes you already feel good in, and whether the session is one person or a team.',
      'I then suggest a place and a time of day. Natural light does most of the work, so I would rather move a session by an hour than fight a harsh midday sun. Cape Town wind and cloud change the plan, and I would rather say so the day before than pretend a wall of glass is flattering.',
      'On the day I give quiet direction. Turn a shoulder, look past the camera, walk while you talk, hold the cup you were already holding. I don\'t ask anyone to fold their arms and smile at a count of three for twenty minutes. People who dislike being photographed usually relax once they realise the session is a conversation with a camera nearby.',
      'I make sure the useful pictures are there as well: a clear head-and-shoulders portrait, a wider picture that shows where you work, and a few frames you can crop for different layouts. For a team I photograph people together and one at a time, so nobody is only ever standing in the back row.',
      'Afterwards I edit the selection and deliver a professionally edited online gallery that you can download and share. I don\'t publish a turnaround promise here, because the edit depends on the session. You\'ll know what to expect when we book.',
      '## Where I photograph',
      'Most business sessions happen on location, not in a hired studio:',
      '- Your own office or studio, when the room, the tools and the people are part of the story.',
      '- A café, restaurant or shop, when the business is the place itself.',
      '- Noordhoek, Kommetjie, Hout Bay and the other Peninsula beaches, when you want light and space rather than a desk.',
      '- A quiet street, a courtyard or a garden in town, when we need a simple background and easy access.',
      'I work across Cape Town. If the right background is a boardroom window, a workshop bench or a stretch of beach, we use that. If you\'re not sure, tell me what the photographs need to do and I\'ll suggest a spot.',
      'The kindest light is usually early morning or the last part of the afternoon. In summer an early start is often easier, especially if we\'re outside. In winter the middle of the day can be soft enough, and I\'ll say so when I see the forecast.',
      '## What to wear for a headshot',
      'Wear something you would actually wear to the meeting these photographs are for. A few things that photograph well:',
      '- Solid colours and simple textures beat loud patterns and big logos. The face should be the first thing someone notices.',
      '- A collar, a knit, a jacket or a clean neckline all work. Choose the version you can sit and stand in without adjusting it every minute.',
      '- Iron the bits that show. Linen creases are fine. A crumpled collar is a different story.',
      '- Bring one alternative layer if you can. A jacket on and a jacket off gives you two useful pictures from the same session.',
      '- Glasses stay on if you wear them every day. I\'ll watch the reflections.',
      'Teams look calmer when people coordinate without matching. Two or three colours across the group is enough. I\'ll send a short note on clothes when you book, so nobody arrives in the outfit they wore to paint the office.',
      '## Headshot and business packages',
      'These are the sessions I currently offer for portraits, headshots and business photography. The price is the price on the package page today:',
      '- [Essence Portrait](/services/essence-portrait): 1 hour, R2,500',
      '- [Express Business Session](/services/express-business-session): 1 hour, R3,000',
      '- [Portrait Story](/services/portrait-story): 2 hours, R4,500',
      '- [Business Branding Photography](/services/business-branding-photography): 2 hours, R5,000',
      '- [Corporate Business Photography](/services/corporate-business-photography): 3 hours, R7,000',
      '- [Premium Business Shoot](/services/premium-business-shoot): 4 hours, R9,000',
      '- [Luxury Business Day](/services/luxury-business-day): 6 hours, R12,500',
      '- [Elite Business Experience](/services/elite-business-experience): 8 hours, R16,000',
      'A short session is enough for one person and a handful of portraits. A longer session is for a team, several setups, or a mix of portraits and photographs of the work itself. You can see everything on my [services page](/services). If the job is bigger or different, ask and I\'ll put a quote together.',
      '## Teams, brands and the photographs you leave with',
      'A single headshot is a portrait of one person. A business session is often more than that. I photograph founders, small teams, the room you work in and the thing you make, so the pictures can sit on a website, a proposal or a social post without looking like they came from three different days.',
      'For a team I keep the pace human. Nobody waits an hour for their turn if I can help it. We do the group pictures while everyone is still fresh, then the individual portraits, then anything that needs the space to itself. If someone is camera-shy, we start with them in the group and come back to a quieter portrait later.',
      'Personal branding is the same idea for one person. We make a portrait you can use on LinkedIn, a wider picture for a talk or a website, and a few frames that show what you actually do. I\'d rather have ten photographs you will use than a hundred you will not.',
      'I don\'t add a per-person rate on this page. If more people join the session, tell me when you enquire and I\'ll say whether the time you have is enough.',
      'Ready to plan the session? [Get in touch](/contact) or call me on [082 415 1652](tel:+27824151652).',
    ].join('\n\n'),
    faqs: [
      {
        question: 'Where do headshots and business sessions take place?',
        answer: 'On location. That might be your office, studio, café or shop, or a beach or garden on the Cape Peninsula. I live in Noordhoek and work across Cape Town. I\'ll help you choose a place that suits the photographs and the weather.',
      },
      {
        question: 'How long does a headshot session take?',
        answer: 'The Express Business Session and the Essence Portrait are each one hour. Longer packages run from two hours up to a full day, which is what I suggest when several people or several setups are involved. The time is listed on each package.',
      },
      {
        question: 'What should I wear?',
        answer: 'Wear something you would wear to the meeting these pictures are for. Solid colours and simple textures work better than busy patterns and large logos. Bring a second layer if you can. I send a short clothing note when you book.',
      },
      {
        question: 'Can you photograph a team, not just one person?',
        answer: 'Yes. For a team I allow time for photographs together and for each person on their own. A longer package is usually the right one, so nobody is rushed and the group pictures are not an afterthought.',
      },
      {
        question: 'Will the photographs work for LinkedIn and a website?',
        answer: 'Yes. I make a clear portrait you can crop for a profile, and wider photographs for a website, a talk or a brand page. You download them from a professionally edited online gallery.',
      },
      {
        question: 'How do I book?',
        answer: 'Send a message through the contact page, or call or WhatsApp me on 082 415 1652. Tell me the date you have in mind, where you would like to shoot, and who is coming. I\'ll confirm whether that day is free and which session fits.',
      },
    ],
  },
};

const HOME_PAGE = {
  title: 'Carmen Lorraine Photography | Cape Town Photographer',
  description: 'Creative, natural photography in Cape Town by Carmen Lorraine. Specialising in portraits, lifestyle, commercial, business and property photography.',
  h1: 'Carmen Lorraine Photography',
  intro: 'I’m a full-time professional photographer living and working in beautiful Cape Town, with 16 years behind the lens. I shoot Brand, Business & Lifestyle as well as Property, Travel & Environmental Photography. 📸\n\nTelling stories of people, places and nature is what I love most.  I’m grateful for life on this incredible planet, and I cherish every day. The sights, sounds, and colours of the earth, the sky above, and the galaxies beyond are what inspire my creativity.',
};

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function filled(value) {
  return Boolean(String(value == null ? '' : value).trim());
}

function truncateDescription(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  const cut = text.slice(0, 155);
  const space = cut.lastIndexOf(' ');
  return (space > 40 ? cut.slice(0, space) : cut).replace(/[\s,;:.-]+$/, '');
}

function currentDescription(source) {
  const text = String(source || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  return `${text.slice(0, 152).trim()}…`;
}

function pickText(stored, fallback, last) {
  if (filled(stored)) return String(stored).trim();
  if (filled(fallback)) return String(fallback).trim();
  return last;
}

function faqItems(faqs) {
  return (Array.isArray(faqs) ? faqs : [])
    .map((item) => ({
      question: String((item && item.question) || '').replace(/\s+/g, ' ').trim(),
      answer: String((item && item.answer) || '').replace(/\s+/g, ' ').trim(),
    }))
    .filter((item) => item.question && item.answer);
}

function resolveCategoryFields(category, name, slug, currentIntro) {
  const defaults = DEFAULT_CATEGORY_CONTENT[slug] || {};
  const storedFaqs = faqItems(category && category.faqs);
  const fallbackFaqs = faqItems(defaults.faqs);
  const hasLiveIntro = filled(category && category.intro) || filled(currentIntro);
  const hasStoredBody = filled(category && category.body);
  return {
    title: pickText(category && category.seoTitle, hasLiveIntro ? '' : defaults.seoTitle, `${name} | Cape Town Photographer`),
    description: filled(category && category.seoDescription)
      ? truncateDescription(category.seoDescription)
      : (filled(currentIntro)
        ? currentDescription(currentIntro)
        : (filled(defaults.seoDescription) ? truncateDescription(defaults.seoDescription) : currentDescription(currentIntro))),
    h1: pickText(category && category.h1, hasLiveIntro ? '' : defaults.h1, name),
    intro: pickText(category && category.intro, currentIntro, defaults.intro),
    body: hasStoredBody ? String(category.body).trim() : (hasLiveIntro ? '' : pickText(defaults.body, '', '')),
    faqs: storedFaqs.length ? storedFaqs : (hasLiveIntro ? [] : fallbackFaqs),
  };
}

function safeHref(url) {
  const raw = String(url || '').trim();
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (raw === 'https://carmenlorraine.com' || raw.startsWith('https://carmenlorraine.com/')) return raw;
  if (/^tel:\+?[0-9]+$/.test(raw)) return raw;
  return '';
}

function inline(text) {
  const source = String(text || '');
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;
  return source.split(pattern).map((part) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return `<strong>${escapeHtml(bold[1])}</strong>`;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      const label = escapeHtml(link[1]);
      if (!href) return label;
      return `<a href="${escapeHtml(href)}">${label}</a>`;
    }
    return escapeHtml(part);
  }).join('');
}

function renderMarkdown(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let list = [];
  const flush = () => {
    if (!list.length) return;
    html.push(`<ul>${list.map((item) => `<li>${inline(item)}</li>`).join('')}</ul>`);
    list = [];
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      return;
    }
    if (trimmed.startsWith('### ')) {
      flush();
      html.push(`<h3>${inline(trimmed.slice(4))}</h3>`);
      return;
    }
    if (trimmed.startsWith('## ')) {
      flush();
      html.push(`<h2>${inline(trimmed.slice(3))}</h2>`);
      return;
    }
    if (trimmed.startsWith('- ')) {
      list.push(trimmed.slice(2));
      return;
    }
    flush();
    html.push(`<p>${inline(trimmed)}</p>`);
  });
  flush();
  return html.join('');
}

function renderIntroHtml(intro) {
  return String(intro || '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${inline(paragraph)}</p>`)
    .join('');
}

function resolveHomePage(stored) {
  const page = stored && typeof stored === 'object' ? stored : {};
  return {
    title: pickText(page.title, HOME_PAGE.title, HOME_PAGE.title),
    description: pickText(page.description, HOME_PAGE.description, HOME_PAGE.description),
    h1: pickText(page.h1, HOME_PAGE.h1, HOME_PAGE.h1),
    intro: pickText(page.intro, HOME_PAGE.intro, HOME_PAGE.intro),
  };
}

function renderHomeIntroHtml(intro) {
  return renderIntroHtml(intro == null ? HOME_PAGE.intro : intro);
}

function renderBodyHtml(body) {
  return renderMarkdown(body);
}

function renderFaqHtml(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return '';
  return `<h2>Frequently asked questions</h2>${items.map((item) => (
    `<h3>${escapeHtml(item.question)}</h3><p>${escapeHtml(item.answer)}</p>`
  )).join('')}`;
}

function renderCategoryContentHtml(filter) {
  const page = filter || {};
  return `${renderIntroHtml(page.intro)}${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function renderBodyAndFaqHtml(filter) {
  const page = filter || {};
  return `${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function faqPageNode(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function faqPageJson(faqs) {
  const node = faqPageNode(faqs);
  if (!node) return '';
  return JSON.stringify(Object.assign({ '@context': 'https://schema.org' }, node));
}

module.exports = {
  SUMMER_SPECIAL,
  DEFAULT_CATEGORY_CONTENT,
  HOME_PAGE,
  escapeHtml,
  truncateDescription,
  resolveCategoryFields,
  resolveHomePage,
  renderIntroHtml,
  renderHomeIntroHtml,
  renderBodyHtml,
  renderFaqHtml,
  renderCategoryContentHtml,
  renderBodyAndFaqHtml,
  faqPageNode,
  faqPageJson,
  safeHref,
};
